import { motion } from 'motion/react'
import React from 'react'

import {
  Dna,
  FolderOpen,
  Mailbox,
  MapTrifold,
  StarFour
} from '@phosphor-icons/react'

import clsx from 'clsx'
import Image from 'next/image'
import AutoMagicMapping from 'web/assets/auto-magic-mapping.webp'
import EnrichWithContext from 'web/assets/enrich-with-context.webp'
import InstantVisualisation from 'web/assets/instant-visualisation.webp'
import SmartDataImport from 'web/assets/smart-data-import.webp'
import SmartSharing from 'web/assets/smart-sharing.webp'

export function HowItWorks() {
  return (
    <div className='pt-[56px] flex gap-[16px] flex-col'>
      <div
        className={clsx(
          'mx-auto flex w-[1005px] items-start justify-center gap-[8px]',
          'max-[991px]:w-[600px]',
          'max-[768px]:w-full max-[768px]:px-[16px]'
        )}
      >
        <h3
          className={clsx(
            'mb-0 text-center text-[52px] leading-[110%] font-[700] tracking-tight',
            'max-[991px]:text-[32px]',
            'max-[768px]:text-[8vw]'
          )}
        >
          Turn your locations, lists and business data into interactive maps in
          seconds — no stress, no setup.
        </h3>
      </div>

      <div
        className={clsx(
          'mx-auto max-w-[1280px] px-0 self-center flex-1 pb-[120px]',
          'max-[768px]:w-full'
        )}
      >
        <HowItWork
          icon={FolderOpen}
          eyebrow='Smart Data Import'
          heading='Bring whatever you’ve got'
          description='Upload a file, paste a list, or connect tools like Stripe, Shopify, or Xero — we’ll handle the rest.'
          cta='Turbo Charge Your Data'
          href='/sign-in'
          src={SmartDataImport}
        />

        <HowItWork
          reverse
          icon={StarFour}
          eyebrow='Auto-Magic Mapping'
          heading='We plot it for you'
          description='Mapstack finds places, regions, and patterns automatically — no coding, no GIS skills.'
          cta='Dive into the Magic'
          href='/sign-in'
          src={AutoMagicMapping}
        />

        <HowItWork
          icon={MapTrifold}
          eyebrow='Instant Visualisation'
          heading='See the patterns'
          description='We automatically highlight what matters — like clusters, hotspots, and groupings — without you lifting a finger.'
          cta='Uncover the Big Picture'
          href='/sign-in'
          src={InstantVisualisation}
        />

        <HowItWork
          reverse
          icon={Dna}
          eyebrow='Enrich with Context'
          heading='Add extra insight, instantly'
          description='Overlay demographic data, regions, or points of interest — with one click.'
          cta='See Data In Context'
          href='/sign-in'
          src={EnrichWithContext}
        />

        <HowItWork
          icon={Mailbox}
          eyebrow='Smart Sharing'
          heading='Show it off (or keep it private)'
          description='Share your map with your team, embed it in a site, export an infographic, or keep it for internal use.'
          cta='Start Showing Off'
          href='/sign-in'
          src={SmartSharing}
        />
      </div>
    </div>
  )
}

interface HowItWorkProps {
  reverse?: boolean
  icon?: any
  eyebrow: string
  heading: string
  description: string
  cta: string
  src: any
  href: string
}

function HowItWork(props: HowItWorkProps) {
  const Icon = props?.icon || React.Fragment

  return (
    <div
      className={clsx(
        'my-[100px] flex items-center justify-between gap-[16px]',
        'max-[991px]:px-[24px]  max-[991px]:items-center max-[991px]:mb-0 max-[991px]:mt-[40px]',
        'max-[768px]:width-[100vw] max-[768px]:flex-col max-[768px]:px-[18px] max-[768px]:items-stretch',
        {
          'flex-row-reverse': props.reverse,
          'flex-row': !props.reverse
        }
      )}
    >
      <div
        className={clsx(
          'mx-[40px] flex max-w-[450px] flex-col items-start',
          'max-[991px]:mx-0 max-[991px]:pl-[16px]',
          'max-[768px]:pl-[36px] max-[768px]:pr-[80px] max-[768px]:max-w-none'
        )}
      >
        <div className='mb-[16px] flex h-[44px] w-auto items-center rounded-[10px] bg-white px-[16px] py-[12px] shadow'>
          <Icon
            weight='duotone'
            size={22}
            className='text-brand-05'
          />
          <div
            className={clsx(
              'text-brand-05 ml-[8px] text-[17px] leading-[110%] font-[700]',
              'max-[991px]:text-[16px]'
            )}
          >
            {props.eyebrow}
          </div>
        </div>
        <div>
          <h2
            className={clsx(
              'mb-[16px] max-w-[514px] text-[36px] leading-[110%] font-[700]',
              'max-[991px]:text-[26px]'
            )}
          >
            {props.heading}
          </h2>
        </div>
        <p
          className={clsx(
            'mb-[24px] tracking-normal text-[19px] leading-[130%] text-gray-400',
            'max-[991px]:text-[15px]'
          )}
        >
          {props.description}
        </p>
        <motion.a
          href={props.href}
          className={clsx(
            'bg-brand-02 rounded-xl px-[24px] py-[16px] text-[17px] leading-[100%] font-[500] text-white'
          )}
          whileHover={{
            scale: 1.1,
            transition: { duration: 0.2 }
          }}
        >
          {props.cta}
        </motion.a>
      </div>
      <div
        className={clsx(
          'relative flex w-[70%] h-full items-center justify-end',
          'max-[991px]:w-auto max-[991px]:max-w-none',
          'max-[768px]:w-full max-[768px]:max-w-none'
        )}
      >
        <Image
          src={props.src}
          alt={props.heading}
          width={558}
          height={339}
          className='max-[768px]:w-full'
        />
      </div>
    </div>
  )
}
